import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Color, FILE_DUPLICATES_PATH, fill, Size, formatDate, hexDigest, inlineRenderFor, t, type PossibleDuplicate } from '@bms2/shared';
import { Button } from '../../components/controls';
import { FileViewer } from '../../components/composites/FileViewer';
import { useFetchedBlob } from '../../components/files/useFetchedBlob';
import { Text } from '../../components/Text';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { get } from './api';
import styles from './DuplicateQuestion.module.css';

/** The two answers (decision 3014): use the file already filed, or upload the chosen one as today. */
export type DuplicateAnswer = 'existing' | 'upload';

interface Asked {
  name: string;
  chosen: Blob;
  duplicate: PossibleDuplicate;
  answer: (choice: DuplicateAnswer) => void;
}

/**
 * The content hash the server stores for a version, taken in the browser before
 * anything is sent. 🔨 A page served without a secure context has no digest, so
 * the question then asks by name alone rather than not at all.
 */
export async function contentHashOf(blob: Blob): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  try {
    return hexDigest(await subtle.digest('SHA-256', await blob.arrayBuffer()));
  } catch {
    // silent: a digest the browser refuses asks by name alone, which is the question's own fallback.
    return null;
  }
}

/** Save the bytes already drawn under the file's own name. */
function save(url: string, name: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
}

/** One side of the comparison: the file's bytes drawn in the application's own viewer, scrolling inside its frame. */
function Side({ name, url, contentType, state }: { name: string; url: string | null; contentType: string; state: 'fetching' | 'failed' | 'drawn' }) {
  const render = state === 'drawn' ? inlineRenderFor(contentType) : null;
  return (
    <FileViewer permission="Download" fileName={name} onDownload={() => (url ? save(url, name) : undefined)} onPrint={() => (url ? window.open(url, '_blank', 'noopener') : undefined)}>
      <div className={styles.stage} data-duplicate-stage={state}>
        {state === 'fetching' ? <Text variant="small">{t('upload.duplicate.reading')}</Text> : null}
        {state === 'failed' || (state === 'drawn' && render === null) ? <Text variant="small">{t('upload.duplicate.cannotDraw')}</Text> : null}
        {state === 'drawn' && url && render === 'image' ? <img className={styles.picture} src={url} alt={name} data-duplicate-document="image" /> : null}
        {state === 'drawn' && url && render === 'frame' ? <iframe className={styles.frame} src={url} title={name} data-duplicate-document="frame" /> : null}
      </div>
    </FileViewer>
  );
}

/** The standing file, streamed through the File Access Proxy as every open does. */
function ExistingSide({ duplicate, apiBaseUrl }: { duplicate: PossibleDuplicate; apiBaseUrl: string }) {
  const bytes = useFetchedBlob(`${apiBaseUrl}/files/${encodeURIComponent(duplicate.id)}/stream`);
  return <Side name={duplicate.filename} url={bytes.kind === 'drawn' ? bytes.url : null} contentType={bytes.kind === 'drawn' ? bytes.contentType : duplicate.mimeType} state={bytes.kind} />;
}

/** The chosen file, drawn from the browser's own copy before anything is sent. */
function ChosenSide({ name, blob }: { name: string; blob: Blob }) {
  // The pattern the picture windows keep: the address made once per file, and given back when the side goes.
  const url = useMemo(() => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);
  return <Side name={name} url={url} contentType={blob.type} state={url ? 'drawn' : 'failed'} />;
}

/**
 * 🔒 The Possible duplicate question (decision 3014; the owner's answer V4a of
 * 2026-10-11). Before an upload sends a file, the window hashes it and asks
 * whether the same content, or failing that the same name, is already filed on
 * the record's company. When it is, the window draws the two side by side, each
 * scrolling in the File Viewer, and asks: use the existing file, which links it
 * to this record and sends nothing, or upload anyway, as today. `check` answers
 * the standing file to link, or null to send the chosen one. Stop ends the wait
 * as an upload never sent, because the aborted request then sends nothing.
 */
export function useDuplicateQuestion(apiBaseUrl: string): { check: (recordType: string, recordId: string, name: string, blob: Blob, signal: AbortSignal) => Promise<PossibleDuplicate | null>; question: ReactNode } {
  const [asked, setAsked] = useState<Asked | null>(null);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();

  const check = useCallback(
    async (recordType: string, recordId: string, name: string, blob: Blob, signal: AbortSignal): Promise<PossibleDuplicate | null> => {
      const contentHash = await contentHashOf(blob);
      const query = new URLSearchParams({ taggedRecordType: recordType, taggedRecordId: recordId, filename: name, ...(contentHash ? { contentHash } : {}) });
      const found = await get<{ duplicate: PossibleDuplicate | null }>(`${FILE_DUPLICATES_PATH}?${query.toString()}`, apiBaseUrl, signal);
      if (signal.aborted) return null;
      // 🔨 A question the server could not answer asks nothing: the file goes as today, which loses nothing.
      if (!found.ok || !found.data.duplicate) return null;
      const duplicate = found.data.duplicate;
      const choice = await new Promise<DuplicateAnswer>((resolve) => {
        const settle = (answer: DuplicateAnswer) => {
          signal.removeEventListener('abort', stopped);
          setAsked(null);
          resolve(answer);
        };
        const stopped = () => settle('upload');
        signal.addEventListener('abort', stopped);
        setAsked({ name, chosen: blob, duplicate, answer: settle });
      });
      return choice === 'existing' && !signal.aborted ? duplicate : null;
    },
    [apiBaseUrl],
  );

  if (!asked) return { check, question: null };
  const { duplicate } = asked;
  const date = formatDate(duplicate.uploadedAt, zone, dateFormat);
  const uploaded = duplicate.uploadedByName ? fill(t('upload.duplicate.uploadedBy'), { date, name: duplicate.uploadedByName ?? '' }) : fill(t('upload.duplicate.uploaded'), { date });
  const question = (
    <div className={styles.question} data-possible-duplicate={duplicate.id} data-duplicate-match={duplicate.matchedBy}>
      <p className={styles.title}>{t('upload.duplicate.title')}</p>
      <p className={styles.text}>{fill(t(duplicate.matchedBy === 'hash' ? 'upload.duplicate.byHash' : 'upload.duplicate.byName'), { record: duplicate.recordLabel })}</p>
      <p className={styles.hint}>{t('upload.duplicate.ask')}</p>
      <div className={styles.pair}>
        <section className={styles.side} data-duplicate-side="existing">
          <p className={styles.caption}>{t('upload.duplicate.existing')}</p>
          {/* 🔒 Above the standing file: its record, its type, and when and by whom it was uploaded. */}
          <p className={styles.facts}>
            <span data-duplicate-record="true">{duplicate.recordLabel}</span>
            <span data-duplicate-type="true">{duplicate.fileTypeName}</span>
            <span data-duplicate-uploaded="true">{uploaded}</span>
          </p>
          <ExistingSide duplicate={duplicate} apiBaseUrl={apiBaseUrl} />
        </section>
        <section className={styles.side} data-duplicate-side="chosen">
          <p className={styles.caption}>{t('upload.duplicate.chosen')}</p>
          <p className={styles.facts}>
            <span>{asked.name}</span>
          </p>
          <ChosenSide name={asked.name} blob={asked.chosen} />
        </section>
      </div>
      <div className={styles.answers}>
        <span data-duplicate-answer="existing">
          <Button size={Size.Small} color={Color.Navy} text={[t('upload.duplicate.useExisting')]} actions={[() => asked.answer('existing')]} />
        </span>
        <span data-duplicate-answer="upload">
          <Button size={Size.Small} color={Color.Inactive} text={[t('upload.duplicate.uploadAnyway')]} actions={[() => asked.answer('upload')]} />
        </span>
      </div>
    </div>
  );
  return { check, question };
}
